import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import type { ZodType } from "zod";

import { cn } from "@/lib/utils";
import {
  Filter,
  ChevronDown,
  X,
  Check,
  Minus,
  TriangleDown,
  TriangleUp,
} from "@/components/icons";
import { cva } from "class-variance-authority";
import {
  SearchField,
  SearchFieldInput,
  SearchFieldClearButton,
} from "@/components/designSystem/SearchField";
import { DropdownScrollArea } from "@/components/designSystem/DropdownScrollArea";

// ==========================================================================
// Filter Context
// ==========================================================================
interface FilterContextValue {
  setParams: (key: string, value: unknown) => void;
  filterValues: Record<string, unknown>;
}

const FilterContext = React.createContext<FilterContextValue | null>(null);

function FilterProvider({
  setParams,
  filterValues,
  children,
}: FilterContextValue & { children: React.ReactNode }) {
  const value = React.useMemo(
    () => ({ setParams, filterValues }),
    [setParams, filterValues],
  );
  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

const filterButtonVariants = cva(
  "relative flex items-center justify-center gap-2.5 overflow-hidden rounded-md px-3 py-2 transition-all",
  {
    variants: {
      status: {
        normal: "bg-static-white inset-border-filter-normal",
        selected: "bg-primary-strong/2 inset-outline-border-gradient",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
    },
    compoundVariants: [
      {
        status: "normal",
        disabled: false,
        className: "active:bg-primary-strong/10",
      },
      {
        status: "selected",
        disabled: false,
        className: "active:bg-primary-strong/10",
      },
    ],
    defaultVariants: {
      status: "normal",
      disabled: false,
    },
  },
);

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterOptionGroup {
  /** 대분류 코드 (예: "CHARGING") */
  value: string;
  /** 대분류 라벨 (예: "충전 문제") */
  label: string;
  /** 소분류 목록 */
  items: FilterOption[];
}

export interface FilterSelectProps {
  /** 필터 라벨 */
  label: string;
  /** 선택 가능한 옵션들 (groups 사용 시 생략) */
  options?: FilterOption[];
  /** 카테고리(대분류) 그룹. 전달 시 아코디언 UI로 렌더되고 options 는 무시된다 */
  groups?: FilterOptionGroup[];
  /** 선택된 값들 (Context 미사용 시) */
  values?: string[];
  /** 값 변경 콜백 (Context 미사용 시) */
  onValuesChange?: (values: string[]) => void;
  /** store 파라미터 키 (Context 사용 시) */
  paramKey?: string;
  /** Zod 검증 스키마 (Context 사용 시) */
  schema?: ZodType;
  /** 필터 아이콘 표시 여부 */
  showFilterIcon?: boolean;
  /** 클리어 버튼 표시 여부 */
  showClearButton?: boolean;
  /** 선택 해제 콜백 */
  onClear?: () => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 추가 className */
  className?: string;
  /** "전체" 옵션 표시 여부 */
  showAllOption?: boolean;
  /** "전체" 옵션 라벨 */
  allOptionLabel?: string;
  /** 검색 입력 표시 여부 */
  showSearch?: boolean;
  /** 검색 placeholder */
  searchPlaceholder?: string;
}

/**
 * 대분류 섹션의 펼침/접힘 애니메이션.
 * 내용 높이를 재서 height 를 보간한다 — `grid-template-rows: 0fr ↔ 1fr` 은 grid item 의
 * 자동 최소 크기에 걸리기 쉬워 명시적인 px 높이를 쓴다.
 */
function CollapsibleSection({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}) {
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = React.useState<number | null>(null);

  // 매 렌더 동기 측정한다 — ResizeObserver 는 문서가 hidden 인 동안 콜백이 오지 않아
  // 최초 측정이 누락될 수 있다. 의존성 배열이 없는 게 의도이며, 아래 동등성 검사가 루프를 끊는다
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useLayoutEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    const next = node.offsetHeight;
    setContentHeight((prev) => (prev === next ? prev : next));
  });

  return (
    <div
      className={cn(
        "overflow-hidden",
        // 측정 전에는 트랜지션을 붙이지 않는다 — 팝오버를 열자마자 첫 섹션이 늘어나 보이지 않게
        contentHeight !== null && "transition-[height] duration-200 ease-out",
      )}
      style={{ height: open ? (contentHeight ?? undefined) : 0 }}
      inert={!open}
    >
      <div ref={contentRef} className="flex flex-col gap-1">
        {children}
      </div>
    </div>
  );
}

type CheckboxStatus = "none" | "checked" | "partial";

interface CheckboxProps {
  status: CheckboxStatus;
  disabled?: boolean;
}

function Checkbox({ status, disabled }: CheckboxProps) {
  const isChecked = status === "checked";
  const isPartial = status === "partial";

  return (
    <div
      className={cn(
        "size-5 rounded-md flex items-center justify-center transition-colors",
        isChecked || isPartial
          ? "bg-primary-normal"
          : "bg-fills-light inset-border-double",
        disabled && "opacity-40",
      )}
    >
      {isChecked && <Check size={16} color="static-white" />}
      {isPartial && <Minus size={16} color="static-white" />}
    </div>
  );
}

function FilterSelect({
  label,
  options: optionsProp = [],
  groups,
  values: valuesProp = [],
  onValuesChange: onValuesChangeProp,
  paramKey,
  schema,
  showFilterIcon = true,
  showClearButton = true,
  onClear,
  disabled = false,
  className,
  showAllOption = true,
  allOptionLabel = "전체 선택",
  showSearch = false,
  searchPlaceholder = "검색어를 입력하세요...",
}: FilterSelectProps) {
  const ctx = React.useContext(FilterContext);

  // groups 가 있으면 소분류를 펼쳐 평탄화한다.
  // 트리거 텍스트·전체 선택·schema 파싱은 평탄화된 목록 기준으로 그대로 동작한다.
  const options = groups ? groups.flatMap((group) => group.items) : optionsProp;

  // Context + paramKey가 있으면 store에서 값 읽기 (boolean 등도 string으로 변환)
  const values =
    paramKey && ctx
      ? ((ctx.filterValues[paramKey] as unknown[])?.map(String) ?? [])
      : valuesProp;

  // 값 변경 처리: Context가 있으면 store에 쓰기
  const onValuesChange = React.useCallback(
    (newValues: string[]) => {
      if (paramKey && ctx) {
        if (newValues.length === 0) {
          ctx.setParams(paramKey, null);
        } else if (schema) {
          const parsed = newValues
            .map((v) => schema.safeParse(v))
            .filter((r) => r.success)
            .map((r) => r.data);
          ctx.setParams(paramKey, parsed.length > 0 ? parsed : null);
        } else {
          ctx.setParams(paramKey, newValues);
        }
      }
      onValuesChangeProp?.(newValues);
    },
    [paramKey, ctx, schema, onValuesChangeProp],
  );

  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  // null이면 사용자가 아직 토글하지 않은 상태 → 첫 대분류만 펼친 기본값을 파생한다.
  // groups 가 비동기로 늦게 도착해도(예: React Query) 매 렌더 다시 계산되어 반영된다.
  const [openGroups, setOpenGroups] = React.useState<string[] | null>(null);
  const effectiveOpenGroups =
    openGroups ?? (groups?.[0] ? [groups[0].value] : []);

  const toggleGroup = (groupValue: string) =>
    setOpenGroups(
      effectiveOpenGroups.includes(groupValue)
        ? effectiveOpenGroups.filter((v) => v !== groupValue)
        : [...effectiveOpenGroups, groupValue],
    );

  // 그룹형 목록에 실제로 그려질 대분류. 구분선을 "마지막 섹션 제외"로 그려야 해서
  // 검색 필터링을 렌더 도중이 아니라 먼저 확정한다
  const visibleGroups = groups
    ? groups
        .map((group) => {
          const query = searchQuery.toLowerCase();
          // 검색 중일 때만 소분류를 필터링한다 (검색 미사용 시 group.items 그대로)
          const groupItems = showSearch
            ? group.items.filter((item) =>
                item.label.toLowerCase().includes(query),
              )
            : group.items;
          const groupLabelMatches = group.label.toLowerCase().includes(query);

          // 소분류도, 대분류 라벨도 검색어와 매치하지 않으면 그룹 자체를 숨긴다
          if (
            showSearch &&
            searchQuery &&
            groupItems.length === 0 &&
            !groupLabelMatches
          ) {
            return null;
          }

          // 검색어로 매치된 소분류가 있으면 접힘 상태와 무관하게 자동으로 펼친다
          // (그렇지 않으면 그룹 헤더만 보이고 매치된 항목이 숨겨진 채로 남는다)
          const isSearchingWithMatches =
            showSearch && !!searchQuery && groupItems.length > 0;
          return {
            group,
            groupItems,
            isExpanded:
              isSearchingWithMatches ||
              effectiveOpenGroups.includes(group.value),
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    : null;

  const isSelected = values.length > 0;
  const selectedCount = values.length;

  const filteredOptions = showSearch
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : options;

  // "전체" 체크박스 상태 계산
  const getAllStatus = (): CheckboxStatus => {
    if (values.length === 0) return "none";
    if (values.length === options.length) return "checked";
    return "partial";
  };

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(false);
    onClear?.();
    onValuesChange?.([]);
  };

  const handleItemClick = (value: string) => {
    if (disabled) return;

    const newValues = values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];

    onValuesChange?.(newValues);
  };

  const handleAllClick = () => {
    if (disabled) return;

    const allStatus = getAllStatus();
    if (allStatus === "checked") {
      // 전체 선택됨 -> 전체 해제
      onValuesChange?.([]);
    } else {
      // 일부 또는 없음 -> 전체 선택
      onValuesChange?.(options.map((opt) => opt.value));
    }
  };

  const getItemStatus = (value: string): CheckboxStatus => {
    return values.includes(value) ? "checked" : "none";
  };

  const getGroupStatus = (group: FilterOptionGroup): CheckboxStatus => {
    const selected = group.items.filter((item) =>
      values.includes(item.value),
    ).length;
    if (selected === 0) return "none";
    if (selected === group.items.length) return "checked";
    return "partial";
  };

  const handleGroupClick = (group: FilterOptionGroup) => {
    if (disabled) return;

    const groupValues = group.items.map((item) => item.value);
    const isAllSelected = getGroupStatus(group) === "checked";

    // 대분류 체크 → 소속 소분류를 모두 펼쳐서 전송 / 해제 → 모두 제거
    const newValues = isAllSelected
      ? values.filter((v) => !groupValues.includes(v))
      : [...new Set([...values, ...groupValues])];

    onValuesChange?.(newValues);
  };

  // 선택된 항목 텍스트 표시
  const getSelectedText = () => {
    if (selectedCount === 0) return null;
    if (selectedCount === options.length) return allOptionLabel;

    // 첫 번째 선택된 항목 찾기 (options 순서 기준)
    const firstSelectedOption = options.find((opt) =>
      values.includes(opt.value),
    );

    // options 가 아직 로딩 전이라 매칭되는 항목이 없으면(예: 새로고침 직후 store 값만 남은 경우)
    // "undefined 외 n개"가 렌더되지 않도록 개수만 표시한다.
    if (!firstSelectedOption) {
      return `${selectedCount}개 선택`;
    }

    if (selectedCount === 1) {
      return firstSelectedOption.label;
    }

    // 2개 이상: "첫번째값 외 n개"
    return `${firstSelectedOption.label} 외 ${selectedCount - 1}개`;
  };

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setSearchQuery("");
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            filterButtonVariants({
              status: isSelected && !disabled ? "selected" : "normal",
              disabled,
            }),
            "group",
            className,
          )}
        >
          {/* Contents */}
          <div
            className={cn("flex items-center gap-1", disabled && "opacity-40")}
          >
            {/* Filter Icon */}
            {showFilterIcon && (
              <Filter
                size={16}
                color={
                  isSelected && !disabled
                    ? "primary-normal"
                    : "labels-alternative"
                }
              />
            )}

            {/* Label */}
            <span
              className={cn(
                "typo-label-1-semibold whitespace-nowrap",
                isSelected && !disabled
                  ? "text-primary-normal"
                  : "text-labels-alternative",
              )}
            >
              {label}
            </span>

            {/* Selected Value */}
            {isSelected && (
              <span
                className={cn(
                  "typo-label-1-semibold whitespace-nowrap",
                  disabled ? "text-labels-alternative" : "text-primary-strong",
                )}
              >
                {getSelectedText()}
              </span>
            )}

            {/* Right Icon */}
            {isSelected && showClearButton ? (
              <span
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={disabled ? undefined : handleClearClick}
                onPointerDown={(e) => {
                  if (disabled) return;
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(false);
                    onClear?.();
                    onValuesChange?.([]);
                  }
                }}
                className={cn(
                  "flex items-center justify-center",
                  disabled ? "cursor-not-allowed" : "cursor-pointer",
                )}
                aria-label="필터 해제"
              >
                <X size={16} color="labels-alternative" />
              </span>
            ) : (
              <ChevronDown size={16} color="labels-alternative" />
            )}
          </div>

          {/* Dim overlay for hover/pressed states */}
          {!disabled && (
            <div
              className={cn(
                "absolute -inset-px pointer-events-none transition-opacity rounded-md",
                "bg-static-black",
                "opacity-0 group-hover:opacity-[0.02] group-active:opacity-10",
              )}
            />
          )}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={cn(
            "bg-static-white text-labels-primary",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            "z-50 w-60 overflow-hidden rounded-xl",
            "inset-border-solid-normal shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
          )}
          sideOffset={4}
          align="start"
        >
          {/* 검색 입력 — 그룹형은 아래 목록과 구분선으로 나뉜다(디자인) */}
          {showSearch && (
            <div
              className={cn(
                "px-2 pt-3 justify-between",
                groups && "pb-3 inset-border-solid-normal-b",
              )}
            >
              <SearchField>
                <SearchFieldInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  onKeyDown={(e) => e.stopPropagation()}
                />
                <SearchFieldClearButton />
              </SearchField>
            </div>
          )}

          {/* 스크롤 영역은 검색 입력 아래에만 둔다 — 스크롤바가 검색 필드를 덮지 않게 */}
          <DropdownScrollArea
            className={groups ? "max-h-95" : "max-h-70"}
            contentClassName={cn(
              "flex flex-col",
              // 그룹형은 대분류 섹션끼리 구분선으로 나뉘어 gap 없이 붙는다
              groups ? "py-0" : "gap-1 px-2 py-3",
            )}
          >
            {/* "전체" 옵션 — 그룹형은 대분류마다 자체 전체 선택을 두므로 노출하지 않는다 */}
            {showAllOption && !groups && !searchQuery && (
              <button
                type="button"
                onClick={handleAllClick}
                disabled={disabled}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg h-8 px-2 py-1",
                  "cursor-pointer outline-none select-none",
                  "hover:bg-fills-primary focus:bg-fills-primary",
                  disabled && "pointer-events-none opacity-50",
                )}
              >
                <div className="shrink-0 size-6 flex items-center justify-center">
                  <Checkbox status={getAllStatus()} disabled={disabled} />
                </div>
                <span className="flex-1 typo-label-1-semibold text-labels-primary text-left truncate">
                  {allOptionLabel}
                </span>
              </button>
            )}

            {/* 옵션 목록 */}
            {visibleGroups
              ? visibleGroups.map(({ group, groupItems, isExpanded }, index) => (
                  // 대분류는 구분선으로 나뉜 섹션이다. 펼친 섹션만 padding 이 넓어지며,
                  // 아래 높이 트랜지션과 같은 시간으로 보간해야 통째로 매끄럽게 늘어난다.
                  // inset-border-* 는 평범한 CSS 클래스라 Tailwind variant(not-last: 등)가
                  // 붙지 않아 마지막 섹션 판정은 JS 로 한다
                  <div
                    key={group.value}
                    className={cn(
                      "flex flex-col gap-1 px-2",
                      "transition-[padding] duration-200 ease-out",
                      isExpanded ? "py-3" : "py-1",
                      index < visibleGroups.length - 1 &&
                        "inset-border-solid-normal-b",
                    )}
                  >
                    {/* 헤더는 접기/펼치기 전용 — 전체 선택은 아래 별도 행이 담당한다 */}
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.value)}
                      disabled={disabled}
                      aria-expanded={isExpanded}
                      className={cn(
                        "flex w-full items-center gap-1 rounded-lg h-8 px-2",
                        "cursor-pointer outline-none select-none",
                        "hover:bg-fills-primary focus:bg-fills-primary",
                        disabled && "pointer-events-none opacity-50",
                      )}
                    >
                      {isExpanded ? (
                        <TriangleUp size={20} color="labels-assistive" />
                      ) : (
                        <TriangleDown size={20} color="labels-assistive" />
                      )}
                      <span className="flex-1 typo-label-2-semibold text-labels-alternative text-left truncate">
                        {group.label}
                      </span>
                    </button>

                    {/* 조건부로 렌더하면 높이가 즉시 튄다 — 항상 렌더하고 높이만 애니메이션한다 */}
                    <CollapsibleSection open={isExpanded}>
                      {/* 검색 중에는 매치된 소분류만 보여 "전체 선택"의 의미가 흐려진다 */}
                      {!searchQuery && (
                        <button
                          type="button"
                          onClick={() => handleGroupClick(group)}
                          disabled={disabled}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-lg h-8 px-2",
                            "cursor-pointer outline-none select-none",
                            "hover:bg-fills-primary focus:bg-fills-primary",
                            disabled && "pointer-events-none opacity-50",
                          )}
                        >
                          <div className="shrink-0 size-6 flex items-center justify-center">
                            <Checkbox
                              status={getGroupStatus(group)}
                              disabled={disabled}
                            />
                          </div>
                          <span className="flex-1 typo-label-1-semibold text-labels-primary text-left truncate">
                            {allOptionLabel}
                          </span>
                        </button>
                      )}
                      {groupItems.map((item) => (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => handleItemClick(item.value)}
                          disabled={disabled}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-lg h-8 px-2",
                            "cursor-pointer outline-none select-none",
                            "hover:bg-fills-primary focus:bg-fills-primary",
                            disabled && "pointer-events-none opacity-50",
                          )}
                        >
                          <div className="shrink-0 size-6 flex items-center justify-center">
                            <Checkbox
                              status={getItemStatus(item.value)}
                              disabled={disabled}
                            />
                          </div>
                          <span className="flex-1 typo-label-1-semibold text-labels-primary text-left truncate">
                            {item.label}
                          </span>
                        </button>
                      ))}
                    </CollapsibleSection>
                  </div>
                ))
              : filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleItemClick(option.value)}
                    disabled={disabled}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-lg h-8 px-2 py-1",
                      "cursor-pointer outline-none select-none",
                      "hover:bg-fills-primary focus:bg-fills-primary",
                      disabled && "pointer-events-none opacity-50",
                    )}
                  >
                    <div className="shrink-0 size-6 flex items-center justify-center">
                      <Checkbox
                        status={getItemStatus(option.value)}
                        disabled={disabled}
                      />
                    </div>
                    <span className="flex-1 typo-label-1-semibold text-labels-primary text-left truncate">
                      {option.label}
                    </span>
                  </button>
                ))}
          </DropdownScrollArea>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

FilterSelect.displayName = "FilterSelect";

export { FilterSelect, FilterProvider, FilterContext };
